import { of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, exhaustMap, withLatestFrom, tap } from 'rxjs/operators';
import { ActionTypes } from '@appStore/account/actions';
import * as actions from '@appStore/account/actions';
import * as fromAccountStore from '@appStore/account/reducers';
import { SettingsService } from '@appStore/account/services/settings.service';
import { MeteringFilter, UserSettings, MeteringPoint, MeteringPointGroup } from '@model';
import { MeteringType, MeteringFilterType } from '@enums';
import { AccountState } from '../reducers';
import { Store } from '@ngrx/store';
import { SxMonitoringService } from 'app/services/sx-monitoring.service';

@Injectable()
export class SettingsEffects {
  constructor(
    private router: Router,
    private settingsService: SettingsService,
    private store: Store<AccountState>,
    private actions$: Actions,
    private monitoringService: SxMonitoringService
  ) {}

  loadSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionTypes.SettingLoad),
      exhaustMap((action) => {
        return this.settingsService.getMySettings().pipe(
          map((settings) => new actions.SettingLoadSuccess(settings)),
          catchError((error) => of(new actions.SettingLoadFail(error)))
        );
      })
    );
  });

  updateFilterSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionTypes.FilterSettingUpdate),
      map((action: actions.FilterSettingUpdate) => action.payload),
      withLatestFrom(this.store.select(fromAccountStore.getSettings)),
      exhaustMap(
        (
          data: [{
            filter: MeteringFilter;
            meteringType: MeteringType;
            userId: string;
            filterArea: string,
            meteringPoints: MeteringPoint[],
            meteringPointGroups?: MeteringPointGroup[] },
            UserSettings]
        ) => {
          let userSettings: UserSettings;
          let filter = data[0].filter;
          switch (data[0].meteringType) {
            case MeteringType.Hot:
              userSettings = { ...data[1], hotMeteringFilter: filter };
              break;
            case MeteringType.Cold:
              userSettings = { ...data[1], coldMeteringFilter: filter };
              break;
            case MeteringType.ColdLogical:
              userSettings = { ...data[1], coldLogicalMeteringFilter: filter };
              break;
            case MeteringType.HotLogical:
              userSettings = { ...data[1], hotLogicalMeteringFilter: filter };
              break;
          }
  
          if (filter.filterType === MeteringFilterType.VirtualFilter || 
            userSettings.hotMeteringFilter.filterType === MeteringFilterType.VirtualFilter || 
            userSettings.coldMeteringFilter.filterType === MeteringFilterType.VirtualFilter) {
            return of(userSettings).pipe(
              map(
                (settings) =>
                  new actions.SettingUpdateSuccess({
                    userSettings: settings,
                    userId: data[0].userId,
                    filterArea: data[0].filterArea,
                    meteringType: data[0].meteringType,
                    meteringPoints: data[0].meteringPoints,
                    meteringPointGroups: data[0]?.meteringPointGroups
                  })
              )
            );
          }
  
           // Returnerar "fel" struktur efter update, därav att vi sätter userSettings istf settings
          return this.settingsService.updateSettings(userSettings).pipe(
            map(
              (settings) =>
                new actions.SettingUpdateSuccess({
                  userSettings: userSettings,
                  userId: data[0].userId,
                  filterArea: data[0].filterArea,
                  meteringType: data[0].meteringType,
                  meteringPoints: data[0].meteringPoints,
                  meteringPointGroups: data[0]?.meteringPointGroups
                })
            ),
            catchError((error) => of(new actions.SettingUpdateFail(error)))
          );
        }
      )
    );
  });

  updateSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionTypes.SettingUpdate),
      map((action: actions.SettingUpdate) => action.payload),
      exhaustMap((userSettings) => {
        return this.settingsService.updateSettings(userSettings.userSettings).pipe(
          map((settings) => new actions.SettingUpdateSuccess(userSettings)),
          catchError((error) => of(new actions.SettingUpdateFail(error)))
        );
      })
    );
  });

  updateSettingsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.SettingUpdateSuccess),
      tap((x: actions.SettingUpdateSuccess) => {

        let settings: MeteringFilter;

        switch (x.payload.meteringType) {
          case MeteringType.Cold:
            settings = x.payload.userSettings.coldMeteringFilter;
          break;
          case MeteringType.ColdLogical:
            settings = x.payload.userSettings.coldLogicalMeteringFilter;
          break;
          case MeteringType.Hot:
            settings = x.payload.userSettings.hotMeteringFilter;
          break;
          case MeteringType.HotLogical:
            settings = x.payload.userSettings.hotLogicalMeteringFilter;
          break;
        default:
          break;
      }


      if (settings) {

        if (settings.filterType === MeteringFilterType.MeteringPoint) {
          let properties = {
            'userId': x.payload.userId,
            'meteringType': x.payload.meteringType,
            'meteringpoint': settings.filterId.toString(),
            'customer': x.payload.meteringPoints?.find(c => c.id === settings.filterId)?.customerId
          }

          this.monitoringService.logEvent('Button MeteringpointFilter ' + x.payload.filterArea, properties);
        }

        if (settings.filterType === MeteringFilterType.MeteringPointGroup) {
          let meteringpointGroup = x.payload.meteringPointGroups?.find(mpg => mpg.id === settings.filterId);
          let customers: string[] = [];

          if (meteringpointGroup) {
            meteringpointGroup.meteringPoints.forEach(mpInGroup => {
              let customer = x.payload.meteringPoints.find(mp => mp.id === mpInGroup.id).customerId;
              if (!customers.includes(customer)) {
                customers.push(customer);
              }
            });
          }

          let properties = {
            'userId': x.payload.userId,
            'meteringType': x.payload.meteringType,
            'meteringpoints': meteringpointGroup?.meteringPoints.map(mp => mp.id).join(','),
            'meteringpointGroup' : settings.filterId.toString(),
            'customers': customers.join(',')
          };

          this.monitoringService.logEvent('Button MeteringpointGroupFilter ' + x.payload.filterArea + ' ' , properties);
        }

        if (settings.filterType === MeteringFilterType.VirtualFilter) {
          let customers: string[] = [];
          settings.containsMeteringPointIds.forEach(containedMp => {
            let customer = x.payload.meteringPoints.find(mp => mp.id === containedMp)?.customerId;
            if (!customers.includes(customer)) {
              customers.push(customer);
            }
          });

          let properties = {
            'userId': x.payload.userId,
            'meteringType': x.payload.meteringType,
            'meteringpoints': settings.containsMeteringPointIds.join(','),
            'customers': customers.join(',')
          };

          this.monitoringService.logEvent('Button MeteringpointVirtualGroupFilter ' + x.payload.filterArea + ' ' , properties);
          }
        }
      })
    ),
   { dispatch: false }
 );
}
